import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AsyncSearchInput } from "../AsyncSearchInput/AsyncSearchInput";
import { SelectOption } from "../AsyncSearchInput/types";
import { TAXONOMY_CLIENT } from "../../../utils/consts";
import {
  VENDOR_NAME_QUERY,
  VENDOR_ID_QUERY,
} from "../../../apis/taxonomy/queries";
import { HgVendor } from "../../../types/taxonomy";
import { useLazyQuery } from "@apollo/client";

export type VendorOption = SelectOption;

interface VendorQueryResponse {
  vendor: {
    data: HgVendor[];
  };
}

export interface VendorInputProps {
  onVendorsSelect: (vendors: VendorOption[]) => void;
  selectedVendorIds?: number[];
}

// Conversion functions
function HgVendorToSelectOption(vendor: HgVendor): VendorOption {
  return { label: vendor.name, value: vendor.id.toString() };
}

function convertHgVendorsToSelectOptionsArray(
  data?: HgVendor[]
): VendorOption[] {
  if (!data || data.length === 0) {
    return [];
  }
  return data.map(HgVendorToSelectOption);
}

// Constants
const DEFAULT_PLACEHOLDER = "Search vendors...";
const NO_VENDORS_MESSAGE = "No vendors found";

export const VendorInput: React.FC<VendorInputProps> = ({
  onVendorsSelect,
  selectedVendorIds = [],
}) => {
  const [currentSelectedVendors, setCurrentSelectedVendors] = useState<
    VendorOption[]
  >([]);

  const [fetchVendorsByIds, { loading }] = useLazyQuery<VendorQueryResponse>(
    VENDOR_ID_QUERY,
    {
      context: { clientName: TAXONOMY_CLIENT },
    }
  );

  const handleOptionsSelect = useCallback(
    (options: VendorOption[]) => {
      setCurrentSelectedVendors(options);
      onVendorsSelect(options);
    },
    [onVendorsSelect]
  );

  const needToFetchVendorIds = useMemo(
    () =>
      selectedVendorIds.length > 0 &&
      !selectedVendorIds.every((id: number) =>
        currentSelectedVendors.some((vendor) => Number(vendor.value) === id)
      ),
    [selectedVendorIds, currentSelectedVendors]
  );

  useEffect(() => {
    if (selectedVendorIds.length === 0) {
      setCurrentSelectedVendors([]);
      return;
    }

    const fetchVendors = async () => {
      const { data } = await fetchVendorsByIds({
        variables: {
          ids: selectedVendorIds,
        },
      });
      if (data?.vendor?.data) {
        const options = convertHgVendorsToSelectOptionsArray(data.vendor.data);
        setCurrentSelectedVendors(options);
      }
    };

    if (needToFetchVendorIds) {
      fetchVendors();
    }
  }, [selectedVendorIds, needToFetchVendorIds, fetchVendorsByIds]);

  return (
    <AsyncSearchInput<VendorOption, HgVendor[]>
      id="vendors"
      query={VENDOR_NAME_QUERY}
      clientName={TAXONOMY_CLIENT}
      searchKey="name"
      selectedOptions={currentSelectedVendors}
      onOptionsSelect={handleOptionsSelect}
      convertResponseToOptions={convertHgVendorsToSelectOptionsArray}
      responseDataPath="vendor.data"
      placeholderText={DEFAULT_PLACEHOLDER}
      noResultsMessage={NO_VENDORS_MESSAGE}
      showGlobalLoadingIndicator={loading}
    />
  );
};
